import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Link as MuiLink,
  Typography,
} from '@mui/material';
import { FaCreditCard } from 'react-icons/fa';
import { useSubscription } from '../hooks/useSubscription';
import { useAuth } from '../auth/AuthProvider';
import { openBillingPortal } from '../services/billingService';
import supabase from '../config/supabaseClient';

const brandPrimary = '#29458d';
const brandPrimaryHover = '#1e3266';

const primaryBtnSx = {
  backgroundColor: brandPrimary,
  color: '#fff',
  textTransform: 'none',
  fontSize: '16px',
  fontWeight: 400,
  padding: '10px 20px',
  borderRadius: '6px',
  boxShadow: 'none',
  '&:hover': { backgroundColor: brandPrimaryHover, boxShadow: 'none' },
  '&.Mui-disabled': { backgroundColor: brandPrimary, color: '#fff', opacity: 0.7 },
};

function formatDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  } catch {
    return null;
  }
}

function StatusChip({ status, isEarly }) {
  const map = {
    trialing: { label: 'On trial', color: 'info' },
    active: { label: isEarly ? 'Active · Early supporter' : 'Active', color: 'success' },
    past_due: { label: 'Payment past due', color: 'warning' },
    canceled: { label: 'Canceled', color: 'default' },
    incomplete: { label: 'Incomplete', color: 'warning' },
    incomplete_expired: { label: 'Expired', color: 'default' },
    unpaid: { label: 'Unpaid', color: 'error' },
    none: { label: 'Core plan', color: 'default' },
  };
  const entry = map[status] || map.none;
  return <Chip size="small" color={entry.color} label={entry.label} />;
}

export default function SubscriptionSection({ onUpgrade }) {
  const { user } = useAuth();
  const { subscription, loading, isActive, isTrialing, trialDaysRemaining } = useSubscription();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Clear phone-number error when user adds phone — poll every 3 seconds
  useEffect(() => {
    if (!error || !user?.id) return;
    const interval = setInterval(async () => {
      const { data } = await supabase.from('profiles').select('phone_number').eq('user_id', user.id).single();
      if (data?.phone_number) setError(null);
    }, 3000);
    return () => clearInterval(interval);
  }, [error, user?.id]);

  const status = subscription?.subscription_status || 'none';
  const isEarly = subscription?.price_tier === 'early_supporter';
  const trialEnd = formatDate(subscription?.trial_end);
  const nextCharge = formatDate(subscription?.current_period_end);
  const canceledAt = formatDate(subscription?.canceled_at);
  // Cancel-at-period-end: subscription is still active/trialing but canceled_at is set
  const pendingCancel = (status === 'trialing' || status === 'active') && !!subscription?.canceled_at;
  // past_due needs Manage Subscription (to update payment method), not Start Free Trial
  const needsPortal = isActive || status === 'past_due' || status === 'unpaid' || status === 'incomplete';
  // Resubscribe: user has had a subscription before — skip trial wording, button says "Subscribe"
  const isResubscribe = !!subscription?.subscription_id;

  const handleUpgrade = async () => {
    setError(null);
    // Check if user has a phone number before showing upgrade modal
    if (user?.id) {
      const { data: prof } = await supabase.from('profiles').select('phone_number').eq('user_id', user.id).single();
      if (!prof?.phone_number) {
        setError('Please add your phone number in the SMS Notifications section below before subscribing.');
        return;
      }
    }
    // Open the UpgradeModal (CTA) instead of going directly to checkout
    if (onUpgrade) {
      onUpgrade();
    }
  };

  const handleManage = async () => {
    setBusy(true);
    setError(null);
    try {
      const { url } = await openBillingPortal();
      if (url) window.location.href = url;
    } catch (err) {
      setError(err.message || 'Could not open billing portal.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box
      sx={{
        mb: 3,
        p: 3,
        borderRadius: 2,
        backgroundColor: 'white',
        boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
      }}
    >
      <Typography
        variant="h5"
        sx={{ display: 'flex', alignItems: 'center', gap: 1, color: brandPrimary, mb: 2 }}
      >
        <FaCreditCard /> Subscription &amp; Billing
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={18} />
          <Typography variant="body2">Loading subscription…</Typography>
        </Box>
      ) : (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>Status:</Typography>
            <StatusChip status={status} isEarly={isEarly} />
          </Box>

          {isTrialing && trialDaysRemaining !== null && !pendingCancel && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {trialDaysRemaining === 0
                ? 'Your trial ends today.'
                : `${trialDaysRemaining} day${trialDaysRemaining === 1 ? '' : 's'} left in your free trial.`}
              {trialEnd && <> Trial ends {trialEnd}.</>}
            </Alert>
          )}

          {pendingCancel && nextCharge && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Your subscription is scheduled to cancel on <strong>{nextCharge}</strong>. You still have access until
              then. To keep your subscription, click <strong>Manage Subscription</strong> and choose "Don't cancel".
            </Alert>
          )}

          {isActive && !isTrialing && nextCharge && (
            <Typography variant="body2" sx={{ mb: 1 }}>
              Next billing date: <strong>{nextCharge}</strong>
            </Typography>
          )}

          {isActive && isTrialing && nextCharge && (
            <Typography variant="body2" sx={{ mb: 1 }}>
              First charge: <strong>{nextCharge}</strong> (Cancel anytime before your trial ends to avoid this charge)
            </Typography>
          )}

          {status === 'canceled' && canceledAt && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Canceled on {canceledAt}. SMS is disabled, but the calculator and downloads remain free.
            </Typography>
          )}

          {status === 'past_due' && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Your last payment failed. Please update your payment method to keep SMS reminders active.
            </Alert>
          )}

          {status === 'none' && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {isResubscribe
                ? "You're on the Core plan. Subscribe to unlock SMS race reminders. $6.99/month. Cancel anytime."
                : "You're on the Core plan. Subscribe to unlock SMS race reminders. 30-day free trial, then $6.99/month. Cancel anytime."}
            </Typography>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
            {needsPortal ? (
              <Button
                variant="contained"
                onClick={handleManage}
                disabled={busy}
                sx={primaryBtnSx}
                startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}
              >
                {busy ? 'Opening…' : status === 'past_due' ? 'Update Payment Method' : 'Manage Subscription'}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleUpgrade}
                disabled={busy}
                sx={primaryBtnSx}
                startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}
              >
                {busy ? 'Redirecting…' : isResubscribe ? 'Subscribe' : 'Start Free Trial'}
              </Button>
            )}
          </Box>

          <Typography color="text.secondary" sx={{ display: 'block', mt: 2, fontSize: '14px', lineHeight: 1.5 }}>
            By subscribing, you authorize Pre Go Prep to charge your payment method the subscription price shown at
            checkout.{' '}
            {isResubscribe
              ? 'You will be charged at the start of each billing period (e.g. monthly) until you cancel, beginning today.'
              : 'You will not be charged until after your free trial ends. After the trial, you will be charged at the start of each billing period (e.g. monthly) until you cancel.'}{' '}
            You may cancel at any time through the subscription or billing management page linked in your profile
            settings; cancellation takes effect at the end of the current billing period. Subscriptions are billed
            monthly unless otherwise specified. We do not provide refunds for any subscription period once it has been
            charged. By completing payment, you agree to our{' '}
            <MuiLink component={RouterLink} to="/terms-of-service">Terms of Service</MuiLink>{' '}
            and{' '}
            <MuiLink component={RouterLink} to="/privacy">Privacy Policy</MuiLink>.
          </Typography>
        </>
      )}
    </Box>
  );
}
